import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, Table, Select, Input, Button, message, Tag, Spin, Divider, Typography,
  List, Popconfirm, Form, Tooltip, Checkbox,
} from 'antd';
import { SaveOutlined, PlusOutlined, DeleteOutlined, TeamOutlined, BellOutlined, UserAddOutlined, StarOutlined, WhatsAppOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import { Link } from 'react-router-dom';
import { getSlotDetail, saveSlotNote, saveSlotAttendances, updateSlotTeachers, getSessionProjects, saveSessionProjects, getSessionDailyWorkNotes } from '../../api/schedule';
import { getStudentNotes, addStudentNote, deleteStudentNote } from '../../api/students';
import { getCourses, getCourseTopics } from '../../api/courses';
import { formatStudentLabel } from '../../utils/studentLabel';
import useAuthStore from '../../store/authStore';
import UserSearchSelect from '../common/UserSearchSelect';
import api from '../../api/axios';
import { apiErrorMessage } from '../../utils/apiError';
import { pickTermForDate, noTermMessage } from '../../utils/termForDate';

dayjs.extend(isBetween);
dayjs.extend(isoWeek);

const { TextArea } = Input;
const { Text } = Typography;

const STATUS_OPTIONS_FULL = [
  { value: 'present',              label: 'Geldi' },
  { value: 'absent',               label: 'Gelmedi' },
  { value: 'planned_absent',       label: 'Mazeretli Devamsız' },
  { value: 'planned_absent_paid',  label: 'Gelmeyecek' },
];
const STATUS_OPTIONS_TEACHER = [
  { value: 'present',              label: 'Geldi' },
  { value: 'absent',               label: 'Gelmedi' },
  { value: 'planned_absent',       label: 'Mazeretli Devamsız' },
  { value: 'planned_absent_paid',  label: 'Gelmeyecek' },
];
const MANAGER_ROLES        = ['developer_admin', 'super_admin', 'branch_manager'];
const CAN_MANAGE_TEACHERS  = ['developer_admin', 'super_admin', 'branch_manager'];
const CAN_ADD_MAKEUP       = ['developer_admin', 'super_admin', 'branch_manager', 'teacher'];
const TYPE_LABELS = { normal: 'Normal', regular: 'Normal', trial: 'Deneme', makeup: 'Telafi', makeup_uncontrolled: 'Kontrolsüz Telafi', one_time: 'Tek Seferlik' };
const TYPE_COLORS = { normal: 'blue', regular: 'blue', trial: 'gold', makeup: 'orange', makeup_uncontrolled: 'volcano', one_time: 'geekblue' };

const StudentNotePanel = ({ studentId }) => {
  const { user } = useAuthStore();
  const ADMIN_ROLES = ['developer_admin', 'super_admin'];
  const [notes, setNotes]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [newNote, setNewNote]     = useState('');
  const [adding, setAdding]       = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    if (!studentId) return;
    setLoading(true);
    getStudentNotes(studentId).then(res => setNotes(res.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [studentId]);

  const handleAdd = async () => {
    if (!newNote.trim()) return;
    setAdding(true);
    try {
      const res = await addStudentNote(studentId, { note: newNote.trim() });
      setNotes(prev => [res.data, ...prev]);
      setNewNote('');
    } catch { message.error('Not eklenemedi.'); }
    finally { setAdding(false); }
  };

  const handleDelete = async (noteId) => {
    try {
      await deleteStudentNote(studentId, noteId);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch { message.error('Silinemedi.'); }
  };

  const startEdit  = (note) => { setEditingId(note.id); setEditingText(note.note); };
  const cancelEdit = ()     => { setEditingId(null); setEditingText(''); };

  const handleSaveEdit = async (noteId) => {
    if (!editingText.trim()) return;
    setSaving(true);
    try {
      await api.put(`/students/${studentId}/notes/${noteId}`, { note: editingText.trim() });
      setNotes(prev => prev.map(n => n.id === noteId ? { ...n, note: editingText.trim() } : n));
      cancelEdit();
    } catch { message.error('Not güncellenemedi.'); }
    finally { setSaving(false); }
  };

  const canEdit = (note) => note.user_id === user?.id || ADMIN_ROLES.includes(user?.role);

  return (
    <div>
      <div className="flex gap-2 mb-2">
        <Input size="small" placeholder="Öğrenciye not ekle..." value={newNote} onChange={e => setNewNote(e.target.value)} onPressEnter={handleAdd} />
        <Button size="small" icon={<PlusOutlined />} loading={adding} onClick={handleAdd} type="primary">Ekle</Button>
      </div>
      {loading ? <Spin size="small" /> : notes.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 12 }}>Henüz not yok.</Text>
      ) : (
        <List size="small" dataSource={notes} renderItem={note => (
          <List.Item style={{ padding: '4px 0' }} actions={
            canEdit(note) ? [
              editingId === note.id ? null : (
                <Button key="edit" type="text" size="small" icon={<span style={{ fontSize: 11 }}>✏️</span>} onClick={() => startEdit(note)} />
              ),
              <Popconfirm key="del" title="Notu sil?" onConfirm={() => handleDelete(note.id)} okText="Sil" cancelText="İptal" okButtonProps={{ danger: true }}>
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>,
            ].filter(Boolean) : []
          }>
            <div style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
              {editingId === note.id ? (
                <div className="flex gap-1">
                  <Input size="small" value={editingText} onChange={e => setEditingText(e.target.value)} onPressEnter={() => handleSaveEdit(note.id)} autoFocus />
                  <Button size="small" type="primary" loading={saving} onClick={() => handleSaveEdit(note.id)}>Kaydet</Button>
                  <Button size="small" onClick={cancelEdit}>İptal</Button>
                </div>
              ) : (
                <>
                  <div className="text-xs" style={{ color: '#374151' }}>{note.note}</div>
                  <div className="text-xs text-gray-400">{note.user?.name} {note.user?.surname} · {dayjs(note.created_at).format('DD.MM.YYYY HH:mm')}</div>
                </>
              )}
            </div>
          </List.Item>
        )} />
      )}
    </div>
  );
};

// Per-student project editor — one row per student. Her blok tek bir
// sertifika + o sertifikadaki konular icin coklu secim (#31): eskiden her
// konu icin ayri "Proje Ekle" tikliyip course+topic secmek gerekiyordu,
// ogretmen ayni derste ayni sertifikadan birden fazla konu isaretleyecekse
// her defasinda ayni "siradaki konu" onerisiyle ugrasiyordu. Ilk halinde
// tum konular checkbox listesi olarak geliyordu ama 40-50 konulu
// sertifikalarda liste asiri uzuyordu (bkz. kullanici geri bildirimi) - Is
// Takip'teki coklu kisi atamasiyla ayni desene (Select mode=multiple)
// gecildi: tamamlanmis konular ustte kompakt yesil etiketler, kalan
// konular icin arattirilabilir coklu secim kutusu, siradaki tamamlanmamis
// konu otomatik secili geliyor.
const StudentProjectRow = ({ student, entries, allCourses, topicsByCourse, completedTopicIds = new Set(), onEntriesChange, onLoadTopics, yarisma = false, onYarismaChange }) => {
  const addBlock    = () => onEntriesChange(student.student_id, [...entries, { course_id: null, checkedTopicIds: [], dailyWorkNote: '' }]);
  const removeBlock = (idx) => onEntriesChange(student.student_id, entries.filter((_, i) => i !== idx));

  const updateCourse = (idx, courseId) => {
    const next = entries.map((e, i) => (i === idx ? { course_id: courseId, checkedTopicIds: [], dailyWorkNote: '' } : e));
    onEntriesChange(student.student_id, next);
    if (courseId) onLoadTopics(courseId);
  };

  const setCheckedTopics = (idx, topicIds) => {
    const next = entries.map((e, i) => (i === idx ? { ...e, checkedTopicIds: topicIds } : e));
    onEntriesChange(student.student_id, next);
  };

  const setDailyWorkNote = (idx, note) => {
    const next = entries.map((e, i) => (i === idx ? { ...e, dailyWorkNote: note } : e));
    onEntriesChange(student.student_id, next);
  };

  const isCourseCompleted = (courseId) => {
    const ct = topicsByCourse[courseId] || [];
    return ct.length > 0 && ct.every(t => completedTopicIds.has(t.id));
  };

  // Yeni (henuz hic isaretlenmemis) bloklarda, konular yuklenince siradaki
  // tamamlanmamis konuyu otomatik isaretler. _initialized ile sadece bir kere
  // calisir - ogretmen isaretlemeyi kaldirsa bile tekrar dayatilmaz. Bagimlilik
  // dizisi entries'in tamamini degil, sadece course_id + konu sayisini izliyor
  // ki kullanicinin manuel checkbox tiklamalari efekti yeniden tetiklemesin.
  const autoInitKey = entries.map(e => `${e.course_id || ''}:${(topicsByCourse[e.course_id] || []).length}`).join('|');
  useEffect(() => {
    entries.forEach((entry, idx) => {
      if (!entry.course_id || entry._initialized) return;
      const topics = topicsByCourse[entry.course_id];
      if (!topics) return;
      const nextTopic = topics.find(t => !completedTopicIds.has(t.id));
      const next = entries.map((e, i) => (i === idx
        ? { ...e, checkedTopicIds: nextTopic ? [nextTopic.id] : e.checkedTopicIds, _initialized: true }
        : e));
      onEntriesChange(student.student_id, next);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoInitKey]);

  return (
    <div className="mb-3 p-2 rounded border bg-gray-50">
      <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
        <span className="text-sm font-medium">{student.student_name}</span>
        <div className="flex items-center gap-2 flex-wrap">
          {/* YARIŞMA ÇALIŞMASI — sertifika programından BAĞIMSIZ.
              Yarışmaya hazırlanan öğrenci ders saatinde müfredat yerine
              yarışma konularına çalışıyor; seçilecek uygun bir sertifika
              programı olmadığı için öğretmen hiçbir şey yazamıyordu.
              Tik, program seçimini gerektirmiyor ve aynı derste müfredat
              çalışmasıyla birlikte de işaretlenebiliyor (çocuk dersin
              yarısında konu bitirip yarısında yarışmaya çalışabiliyor). */}
          <Checkbox
            checked={yarisma}
            onChange={e => onYarismaChange(student.student_id, e.target.checked)}
          >
            <span className="text-xs">Yarışma Çalışması</span>
          </Checkbox>
          <Button size="small" icon={<PlusOutlined />} onClick={addBlock}>Proje Ekle</Button>
        </div>
      </div>
      {entries.length === 0 && !yarisma && (
        <div className="text-xs text-gray-400">Henüz proje girilmedi.</div>
      )}
      {entries.map((entry, idx) => {
        const courseOptions = allCourses.map(c => {
          const done = isCourseCompleted(c.id);
          return { value: c.id, label: c.name, disabled: done && entry.course_id !== c.id, _done: done };
        });
        const topics = topicsByCourse[entry.course_id] || [];
        return (
          <div key={idx} className="mb-2 p-2 rounded bg-white border">
            <div className="flex gap-2 mb-1 items-center">
              <Select
                size="small"
                style={{ flex: 1 }}
                placeholder="Sertifika seç"
                value={entry.course_id}
                onChange={val => updateCourse(idx, val)}
                showSearch
                optionFilterProp="label"
                options={courseOptions}
                optionRender={(opt) => (
                  <span style={opt.data._done ? { color: '#52c41a', fontWeight: 500 } : {}}>
                    {opt.label}{opt.data._done ? ' ✓ Tamamlandı' : ''}
                  </span>
                )}
              />
              <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeBlock(idx)} />
            </div>
            {entry.course_id && (
              topics.length === 0 ? (
                <div className="text-xs text-gray-400 px-1">Bu sertifikada tanımlı konu yok.</div>
              ) : (
                <div className="pl-1">
                  <Select
                    mode="multiple"
                    size="small"
                    style={{ width: '100%' }}
                    placeholder="Bugün işlenen konular"
                    value={entry.checkedTopicIds}
                    onChange={val => setCheckedTopics(idx, val)}
                    showSearch
                    optionFilterProp="label"
                    options={topics.map(t => ({ value: t.id, label: t.name, _done: completedTopicIds.has(t.id) }))}
                    optionRender={(opt) => (
                      <span style={opt.data._done ? { color: '#52c41a', fontWeight: 500 } : {}}>
                        {opt.label}{opt.data._done ? ' ✓' : ''}
                      </span>
                    )}
                    tagRender={({ label, value, closable, onClose }) => {
                      const done = completedTopicIds.has(value);
                      return (
                        <Tag
                          color={done ? 'green' : 'default'}
                          closable={closable}
                          onClose={onClose}
                          style={{ marginInlineEnd: 4, fontSize: 11 }}
                        >
                          {label}
                        </Tag>
                      );
                    }}
                  />
                </div>
              )
            )}
            {entry.course_id && (
              <div className="pl-1 mt-1.5">
                <Input
                  size="small"
                  placeholder="Bugünkü çalışma (ör. kediyle ilgili proje, opsiyonel)"
                  value={entry.dailyWorkNote || ''}
                  onChange={e => setDailyWorkNote(idx, e.target.value)}
                  maxLength={2000}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * onChanged: bu modalda yapılan bir işlem takvim ızgarasındaki DOLULUK
 * sayısını (9/10 gibi) değiştirdiğinde çağrılır — takvimin kendini
 * tazelemesi için.
 *
 * Gerekliydi çünkü modal yalnızca kendi içeriğini tazeliyordu (fetchSlot);
 * öğrenci eklenince/gelmeyecek işaretlenince arkadaki takvim eski sayıda
 * kalıyor, kullanıcı düzelsin diye tüm sayfayı yeniliyordu. Bir sayfa
 * yenilemesi 24-43 istek; buradaki tazeleme gün görünümünde 1, haftada en
 * fazla 7 istek — hem daha ucuz hem sadece gerçekten değişiklik olunca.
 */
const SlotDetailModal = ({ open, onClose, onDelete, onChanged, date, classroomId, startTime, classroomName, autoOpenMakeup = false }) => {
  const { user }    = useAuthStore();
  const isManager   = MANAGER_ROLES.includes(user?.role);
  const statusOptions = isManager ? STATUS_OPTIONS_FULL : STATUS_OPTIONS_TEACHER;

  const [loading, setSloading]   = useState(false);
  const [saving, setSaving]      = useState(false);
  const [deleting, setDeleting]  = useState(false);
  const [slotData, setSlotData]  = useState(null);
  const [rows, setRows]          = useState([]);
  const [slotNote, setSlotNote]  = useState('');
  const [sessionId, setSessionId] = useState(null);
  // fetchProjects birden fazla yerden (ilk acilis + kaydet sonrasi refetch)
  // ust uste binebilecek sekilde cagriliyor - eski (yavas) bir cagri, yeni
  // (hizli) bir cagridan SONRA cozulup studentProjects'i eski veriyle
  // ezebiliyordu (#69 sonrasi fark edilen "bazen ID gorunuyor" sorununun
  // bir parcasi). Bu ref ile sadece EN SON baslatilan cagrinin sonucu
  // state'e yaziliyor.
  const fetchProjectsSeq = useRef(0);
  const fetchCompletedTopicsSeq = useRef(0);

  // Teacher assignment
  const [selectedTeachers, setSelected] = useState([]);
  const [savingTeachers, setSavingT]    = useState(false);

  // Per-student projects: { [studentId]: [{course_id, checkedTopicIds}] }
  const [studentProjects, setStudentProjects]   = useState({});
  // Yarışma çalışması tiki — sertifika bloklarından BAĞIMSIZ, öğrenci bazında.
  // `yarismaYuklenen`, modal açılırken sunucudan gelen hali tutuyor: tik
  // kaldırıldığında sunucuya "bu öğrencinin girdisini sıfırla" diyebilmek için
  // lazım (öğrencinin başka kaydı yoksa istekte hiç görünmez ve eski satır
  // silinmeden kalırdı).
  const [yarismaByStudent, setYarismaByStudent] = useState({});
  const [yarismaYuklenen, setYarismaYuklenen]   = useState({});
  const [topicsByCourse, setTopicsByCourse]     = useState({});
  const [allCourses, setAllCourses]             = useState([]);
  const [loadingProjects, setLoadingProjects]   = useState(false);
  const [savingProjects, setSavingProjects]     = useState(false);
  const [studentCompletedTopics, setStudentCompletedTopics] = useState({});

  // Notify parents
  const [notifyOpen, setNotifyOpen]       = useState(false);
  const [notifying, setNotifying]         = useState(false);
  const [notifyForm]                      = Form.useForm();

  // Öğrenci ekle
  const [addStudentOpen, setAddStudentOpen]   = useState(false);
  const [studentSearch, setStudentSearch]     = useState('');
  const [studentResults, setStudentResults]   = useState([]);
  const [searchingStudents, setSearching]     = useState(false);
  const [addingStudent, setAddingStudent]     = useState(false);
  const [addStudentType, setAddStudentType]   = useState('one_time');
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Telafi ekleme
  const [makeupOpen, setMakeupOpen]               = useState(false);
  const [pendingMakeups, setPendingMakeups]       = useState([]);
  const [loadingMakeups, setLoadingMakeups]       = useState(false);
  const [selectedMakeup, setSelectedMakeup]       = useState(null);
  const [schedulingMakeup, setSchedulingMakeup]   = useState(false);

  useEffect(() => {
    if (open && date && classroomId && startTime) {
      fetchSlot();
      fetchCourses();
    }
    if (!open) setMakeupOpen(false);
  }, [open, date, classroomId, startTime]);

  const autoMakeupTriggered = React.useRef(false);
  useEffect(() => {
    if (!open) { autoMakeupTriggered.current = false; return; }
    if (autoOpenMakeup && slotData && !autoMakeupTriggered.current) {
      autoMakeupTriggered.current = true;
      openMakeupModal();
    }
  });

  const fetchSlot = async () => {
    setSloading(true);
    try {
      const res  = await getSlotDetail({ date, classroom_id: classroomId, start_time: startTime });
      const data = res.data;
      setSlotData(data);
      setSlotNote(data.note || '');
      setSelected(data.teachers?.map(t => t.id) || []);
      const sorted = [...data.students].sort((a, b) => {
        // en eski videolu (null = hiç video yok) en üste
        if (!a.last_video_at && !b.last_video_at) return 0;
        if (!a.last_video_at) return -1;
        if (!b.last_video_at) return 1;
        return new Date(a.last_video_at) - new Date(b.last_video_at);
      });
      setRows(sorted.map(s => ({
        ...s,
        _status: s.attendance?.status || null,
      })));
      const sid = data.session_id || data.session_ids?.[0] || null;
      setSessionId(sid);
      const allSids = data.session_ids?.length ? data.session_ids : (sid ? [sid] : []);
      if (allSids.length) fetchProjects(allSids);
      const studentIds = data.students.map(s => s.student_id);
      if (studentIds.length) fetchCompletedTopics(studentIds);
    } catch { message.error('Slot detayı yüklenemedi.'); }
    finally { setSloading(false); }
  };

  const fetchCourses = async () => {
    try {
      const res = await api.get(`/classrooms/${classroomId}/courses`);
      setAllCourses(res.data || []);
    } catch {}
  };

  const fetchProjects = async (sids) => {
    const mySeq = ++fetchProjectsSeq.current;
    setLoadingProjects(true);
    try {
      const sidArr = Array.isArray(sids) ? sids : [sids];
      const [projectResults, noteResults] = await Promise.all([
        Promise.all(sidArr.map(sid => getSessionProjects(sid).then(r => r.data || []).catch(() => []))),
        Promise.all(sidArr.map(sid => getSessionDailyWorkNotes(sid).then(r => r.data || []).catch(() => []))),
      ]);
      const data = projectResults.flat();
      const notes = noteResults.flat();
      // Build per-student map: {studentId: [{course_id, checkedTopicIds, dailyWorkNote}]} -
      // ayni sertifikaya ait tum kayitlar (#31 checklist tasarimi) tek bir
      // bloga toplaniyor, konular checkedTopicIds icinde birikiyor.
      const map = {};
      const blockByStudentCourse = {};
      const referencedCourseIds = new Set();
      const yarismaMap = {};
      data.forEach(p => {
        // Yarışma satırının course_id'si NULL — blok kurma mantığına
        // sokulursa boş bir "sertifika seçiniz" bloğu çizilir. Ayrı tutuluyor.
        if (p.type === 'competition') {
          yarismaMap[p.student_id] = true;
          return;
        }
        if (!map[p.student_id]) map[p.student_id] = [];
        const key = `${p.student_id}_${p.course_id}`;
        let block = blockByStudentCourse[key];
        if (!block) {
          block = { course_id: p.course_id, checkedTopicIds: [], dailyWorkNote: '', _initialized: true };
          blockByStudentCourse[key] = block;
          map[p.student_id].push(block);
        }
        if (p.topic_id && !block.checkedTopicIds.includes(p.topic_id)) block.checkedTopicIds.push(p.topic_id);
        if (p.course_id) referencedCourseIds.add(p.course_id);
      });
      // "Bugünkü Çalışma" notlarını (#32) aynı öğrenci+sertifika bloğuna
      // yerleştir — proje bloğu henüz yoksa (nadir: sadece not girilmiş
      // eski bir kayıt) yeni bir blok olarak ekle.
      notes.forEach(n => {
        if (!map[n.student_id]) map[n.student_id] = [];
        const key = `${n.student_id}_${n.course_id}`;
        let block = blockByStudentCourse[key];
        if (!block) {
          block = { course_id: n.course_id, checkedTopicIds: [], dailyWorkNote: '', _initialized: true };
          blockByStudentCourse[key] = block;
          map[n.student_id].push(block);
          if (n.course_id) referencedCourseIds.add(n.course_id);
        }
        block.dailyWorkNote = n.note;
      });
      // Konu adlarini (topicsByCourse) setStudentProjects'ten ONCE yukleyip
      // bekliyoruz - eskiden loadTopicsForCourse burada await edilmeden
      // (fire-and-forget) cagriliyordu, bu yuzden secili konu ID'leri
      // (checkedTopicIds) ekrana topics henuz yuklenmeden basiliyordu ve
      // AntD Select karsilik gelen "label"i bulamadigi icin ham ID'yi
      // gosteriyordu (#69 sonrasi fark edilen ayri bir sorun - "iki kere
      // tetiklenince ID gorunuyor").
      await Promise.all([...referencedCourseIds].map(id => loadTopicsForCourse(id)));
      // Bu cagri suren asenkron isler sirasinda daha yeni bir fetchProjects
      // baslatilmissa (ör. ilk acilis + kaydet-sonrasi refetch ust uste
      // binmis), eski/yavas cagrinin sonucu state'i ezmesin diye burada
      // vazgeciliyor - sadece EN SON baslatilan cagri state'e yaziyor.
      if (fetchProjectsSeq.current !== mySeq) return;
      setStudentProjects(map);
      setYarismaByStudent(yarismaMap);
      setYarismaYuklenen(yarismaMap);
    } catch {}
    finally { if (fetchProjectsSeq.current === mySeq) setLoadingProjects(false); }
  };

  const fetchCompletedTopics = async (studentIds) => {
    // fetchProjects'teki ayni sequence-guard deseni - modal acilisindaki ilk
    // cagri ile kaydet-sonrasi cagri ust uste binebiliyordu, yavas olan (eski)
    // cagri gec dondugunde yeni sonucu eziyordu - az once kaydedilen konu
    // gecici olarak "tamamlanmamis" (siyah) gorunuyordu, sayfa yenilenince
    // (tek ve dogru cagri kalinca) duzeliyordu.
    const mySeq = ++fetchCompletedTopicsSeq.current;
    const results = await Promise.all(
      studentIds.map(id =>
        api.get(`/students/${id}/projects`)
          .then(r => ({ id, topicIds: new Set((r.data?.projects || []).map(p => p.topic_id).filter(Boolean)) }))
          .catch(() => ({ id, topicIds: new Set() }))
      )
    );
    if (fetchCompletedTopicsSeq.current !== mySeq) return;
    setStudentCompletedTopics(prev => {
      const next = { ...prev };
      results.forEach(({ id, topicIds }) => { next[id] = topicIds; });
      return next;
    });
  };

  const loadTopicsForCourse = async (courseId) => {
    if (!courseId || topicsByCourse[courseId]) return;
    try {
      const res = await getCourseTopics(courseId);
      setTopicsByCourse(prev => ({ ...prev, [courseId]: res.data || [] }));
    } catch {}
  };

  const handleProjectsChange = (studentId, entries) => {
    setStudentProjects(prev => ({ ...prev, [studentId]: entries }));
  };

  const handleYarismaChange = (studentId, checked) => {
    setYarismaByStudent(prev => ({ ...prev, [studentId]: checked }));
  };

  const handleSaveProjects = async () => {
    if (!sessionId) { message.error('Session bulunamadı.'); return; }
    // Her öğrencinin projesi kendi session_id'sine yazılmalı — slotun ilk
    // session_id'sine hepsini yazmak, sadece o session'daki öğrencinin
    // projesinin sayılmasına (ör. calendar'da 1/10 görünmesine) yol açar.
    const sessionIdByStudent = {};
    rows.forEach(r => { sessionIdByStudent[r.student_id] = r.session_id; });

    // Onceden burada checkedTopicIds, ogrencinin BUGUNE KADAR tamamladigi
    // (studentCompletedTopics) konulara gore filtreleniyordu - ama bir
    // kaydetme sonrasi fetchCompletedTopics hemen yeniden cekildigi icin az
    // once kaydedilen konular da "tamamlanmis" sayilir hale geliyordu. Ayni
    // modalda ikinci bir kaydetme (ör. baska bir ogrenci icin Kaydet'e
    // basmak, hepsini yeniden gonderiyor) bu durumda checkedTopicIds'i bos
    // sanip topic_id:null fallback'ine dusuyor, backend'in replace
    // (delete+insert) davranisiyla birlesince ogrencinin az once kaydedilen
    // secimini SILIYORDU. Artik checkedTopicIds oldugu gibi gonderiliyor.
    const bySession = {};
    const notesBySession = {};
    Object.entries(studentProjects).forEach(([studentId, blocks]) => {
      const sid = sessionIdByStudent[parseInt(studentId)] || sessionId;
      blocks.filter(b => b.course_id).forEach(b => {
        const topicIds = b.checkedTopicIds;
        if (topicIds.length === 0) {
          (bySession[sid] ||= []).push({ student_id: parseInt(studentId), course_id: b.course_id, topic_id: null });
        } else {
          topicIds.forEach(tid => {
            (bySession[sid] ||= []).push({ student_id: parseInt(studentId), course_id: b.course_id, topic_id: tid });
          });
        }
        // "Bugünkü Çalışma" serbest metni (#32) - resmi konu tamamlama
        // sayilmiyor, sadece veli tarafinda X/Y kesirine +1/+1 yansitiliyor.
        if (b.dailyWorkNote && b.dailyWorkNote.trim()) {
          (notesBySession[sid] ||= []).push({ student_id: parseInt(studentId), course_id: b.course_id, note: b.dailyWorkNote.trim() });
        }
      });
    });

    // Yarışma çalışması — sertifika programı YOK, konu YOK. Öğrencinin
    // sertifika bloğu olmasa da gönderilir (yalnızca tik işaretlenmiş olabilir).
    const clearStudentIds = [];
    Object.entries(yarismaByStudent).forEach(([studentId, isaretli]) => {
      const sid = sessionIdByStudent[parseInt(studentId)] || sessionId;
      if (isaretli) {
        (bySession[sid] ||= []).push({
          student_id: parseInt(studentId),
          course_id: null,
          topic_id: null,
          type: 'competition',
        });
      } else if (yarismaYuklenen[studentId]) {
        // Tik kaldırıldı. Öğrencinin başka girdisi yoksa istekte hiç
        // görünmeyeceği için sunucu eski satırını silemezdi — açıkça söyle.
        clearStudentIds.push(parseInt(studentId));
      }
    });

    // Yalnızca "tik kaldırıldı" durumu için istek üret.
    clearStudentIds.forEach(studentId => {
      const sid = sessionIdByStudent[studentId] || sessionId;
      bySession[sid] ||= [];
    });

    setSavingProjects(true);
    try {
      // allSettled: bir session'daki kayit basarisiz olsa bile digerlerinin
      // basarili olan kismi kaybolmasin (Promise.all hepsini reddediyordu,
      // kismi hata "hicbiri kaydedilmedi" gibi yanlis bir izlenim veriyordu).
      const entries = Object.entries(bySession);
      const results = await Promise.allSettled(
        entries.map(([sid, arr]) => {
          // Bu seansa ait olup tiki kaldırılan öğrenciler — sunucu onların
          // eski yarışma satırını ancak böyle silebiliyor.
          const temizlenecek = clearStudentIds.filter(
            id => (sessionIdByStudent[id] || sessionId) === parseInt(sid)
          );
          return saveSessionProjects(sid, {
            student_projects: arr,
            ...(notesBySession[sid] ? { daily_work_notes: notesBySession[sid] } : {}),
            ...(temizlenecek.length ? { clear_student_ids: temizlenecek } : {}),
          });
        })
      );

      const failedSessions = results
        .map((r, i) => (r.status === 'rejected' ? entries[i][0] : null))
        .filter(Boolean);

      // Kaydetme sonrasi ekrani sunucudaki gercek durumla yeniden senkronize
      // et - boylece "kaydedildi mi edilmedi mi" belirsizligi kalmiyor,
      // herhangi bir tutarsizlik hemen goze carpar.
      const allSids = slotData?.session_ids?.length ? slotData.session_ids : (sessionId ? [sessionId] : []);
      if (allSids.length) await fetchProjects(allSids);
      const savedStudentIds = [...new Set(entries.flatMap(([, arr]) => arr.map(p => p.student_id)))];
      if (savedStudentIds.length) await fetchCompletedTopics(savedStudentIds);

      if (failedSessions.length === 0) {
        message.success('Projeler kaydedildi.');
      } else {
        message.error(`${failedSessions.length} kayıt başarısız oldu, diğerleri kaydedildi. Lütfen tekrar deneyin.`);
      }
    } catch { message.error('Projeler kaydedilemedi.'); }
    finally { setSavingProjects(false); }
  };

  const updateRow = (sessionId, field, value) =>
    setRows(prev => prev.map(r => r.session_id === sessionId ? { ...r, [field]: value } : r));

  const searchStudents = async (q) => {
    setStudentSearch(q);
    if (!q || q.length < 2) { setStudentResults([]); return; }
    setSearching(true);
    try {
      const res = await api.get('/students', { params: { search: q, per_page: 20, status: ['active', 'candidate'] } });
      setStudentResults(res.data.data || []);
    } catch { setStudentResults([]); }
    finally { setSearching(false); }
  };

  const handleAddStudent = async () => {
    if (!selectedStudent || !slotData) return;
    setAddingStudent(true);
    try {
      // Dönem, seansın TARİHİNDEN belirlenir. Eskiden burada `|| terms[0]`
      // yedeği vardı: tarih hiçbir aktif dönemin aralığında değilse kayıt,
      // o tarihi kapsamayan bir döneme bağlanıyordu ve hata mesajı hiç
      // görünmüyordu (liste boş olmadıkça term hep dolu geliyordu).
      // 13 Ağustos'a açılan, 7 Eylül'de başlayan dönemli ders bu yüzden
      // oluştu. Yedeği geri EKLEMEYİN.
      const termRes = await api.get('/terms', { params: { classroom_id: classroomId, is_active: 1, per_page: 100 } });
      const terms = termRes.data.data || [];
      const term = pickTermForDate(terms, date);
      if (!term) { message.error(noTermMessage(date)); return; }

      const [h, m] = startTime.split(':').map(Number);
      const endTime = `${String(h + 1).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

      await api.post('/schedule/enroll', {
        student_id:   selectedStudent.id,
        term_id:      term.id,
        classroom_id: parseInt(classroomId),
        day_of_week:  dayjs(date).isoWeekday(),
        start_time:   startTime,
        end_time:     endTime,
        type:         addStudentType,
        start_date:   date,
      });
      message.success(`${selectedStudent.name} ${selectedStudent.surname} eklendi.`);
      setAddStudentOpen(false);
      setSelectedStudent(null);
      setStudentSearch('');
      setStudentResults([]);
      setAddStudentType('one_time');
      fetchSlot();
      onChanged?.();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Öğrenci eklenemedi.');
    } finally {
      setAddingStudent(false);
    }
  };

  const handleSaveAttendances = async () => {
    const toSave = rows.filter(r => r._status && r.session_id);
    if (!toSave.length) { message.warning('En az bir öğrenci için yoklama durumu seçin.'); return; }
    setSaving(true);
    try {
      await saveSlotAttendances({
        date:         date,
        classroom_id: classroomId,
        start_time:   startTime,
        attendances:  toSave.map(r => ({
          session_id: r.session_id,
          student_id: r.student_id,
          status:     r._status,
        })),
      });
      message.success('Yoklama kaydedildi.');
      fetchSlot();
      // "Gelmeyecek" işaretlenen öğrenci doluluktan düşüyor - takvimdeki
      // sayı da güncellenmeli.
      onChanged?.();
    } catch { message.error('Yoklama kaydedilemedi.'); }
    finally { setSaving(false); }
  };

  const handleSaveNote = async () => {
    setSaving(true);
    try {
      await saveSlotNote({ date, classroom_id: classroomId, start_time: startTime, note: slotNote || null });
      message.success('Not kaydedildi.');
    } catch { message.error('Not kaydedilemedi.'); }
    finally { setSaving(false); }
  };

  const handleSaveTeachers = async () => {
    setSavingT(true);
    try {
      await updateSlotTeachers({ date, classroom_id: classroomId, start_time: startTime, teacher_ids: selectedTeachers });
      message.success('Öğretmenler güncellendi.');
    } catch { message.error('Güncellenemedi.'); }
    finally { setSavingT(false); }
  };

  const handleNotifyParents = async (values) => {
    if (!sessionId) { message.error('Session bulunamadı.'); return; }
    setNotifying(true);
    try {
      const res = await api.post(`/lesson-sessions/${sessionId}/notify-parents`, values);
      message.success(res.data?.message || 'Bildirim gönderildi.');
      notifyForm.resetFields();
      setNotifyOpen(false);
    } catch (e) {
      message.error(apiErrorMessage(e, 'Bildirim gönderilemedi.'));
    } finally {
      setNotifying(false);
    }
  };

  const openMakeupModal = async () => {
    setMakeupOpen(true);
    setSelectedMakeup(null);
    setLoadingMakeups(true);
    try {
      // classroom_id ŞART: uç şubeyi bundan çözüyor. Gönderilmezse süper
      // adminde hiçbir şube filtresi uygulanmıyor ve başka şubelerin
      // öğrencileri listeleniyor (03.09.2026'da görüldü).
      const res = await api.get('/makeup-rights/pending', {
        params: { classroom_id: classroomId },
      });
      setPendingMakeups(res.data || []);
    } catch { message.error('Telafi hakları yüklenemedi.'); }
    finally { setLoadingMakeups(false); }
  };

  const handleScheduleMakeup = async () => {
    if (!selectedMakeup) return;
    setSchedulingMakeup(true);
    try {
      const makeup = pendingMakeups.find(m => m.id === selectedMakeup);
      if (!makeup) return;

      // 1. Öğrenciyi bu slota makeup olarak ekle
      // Bkz. handleAddStudent — `|| terms[0]` yedeği kaldırıldı, telafi dersi
      // artık yalnızca tarihi kapsayan döneme bağlanabiliyor.
      const termRes = await api.get('/terms', { params: { classroom_id: classroomId, is_active: 1, per_page: 100 } });
      const terms = termRes.data.data || [];
      const term = pickTermForDate(terms, date);
      if (!term) { message.error(noTermMessage(date)); return; }

      const [h, m] = startTime.split(':').map(Number);
      const endTime = `${String(h + 1).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

      // Hakkı ayırmak artık SUNUCUNUN işi (MakeupRightService::planla) —
      // kaydı açan istekle aynı transaction'da yapılıyor. Buradaki ikinci
      // `PUT /makeup-rights/{id}` çağrısı KALDIRILDI: sunucu hakkı zaten
      // tükettiği için ikinci çağrı bir hakkı daha harcardı.
      //
      // `makeup_right_id` gönderiliyor çünkü kullanıcı bu ekranda hakkı
      // devamsızlık tarihine bakarak SEÇİYOR; gönderilmezse sunucu en eski
      // hakkı kullanır ve seçim sessizce yok sayılırdı.
      await api.post('/schedule/enroll', {
        student_id:      makeup.student_id,
        term_id:         term.id,
        classroom_id:    parseInt(classroomId),
        day_of_week:     dayjs(date).isoWeekday(),
        start_time:      startTime,
        end_time:        endTime,
        type:            'makeup',
        start_date:      date,
        makeup_right_id: makeup.id,
      });

      message.success(`${makeup.student?.name} ${makeup.student?.surname} telafi olarak eklendi.`);
      setMakeupOpen(false);
      setSelectedMakeup(null);
      fetchSlot();
      onChanged?.();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Telafi eklenemedi.');
    } finally {
      setSchedulingMakeup(false);
    }
  };

  const handleDeleteSlot = async () => {
    setDeleting(true);
    try {
      const ids = slotData?.session_ids || (sessionId ? [sessionId] : []);
      await Promise.all(ids.map(id => api.delete(`/lesson-sessions/${id}`)));
      message.success('Ders silindi.');
      onDelete?.();
      onClose();
    } catch { message.error('Ders silinemedi.'); }
    finally { setDeleting(false); }
  };

  const attendanceColumns = [
    {
      title: 'Öğrenci',
      key: 'student',
      width: 200,
      render: (_, r) => (
        <div>
          <span className="inline-flex items-center gap-1">
            {r.student_id ? (
              <Link
                to={`/students/${r.student_id}`}
                className="font-medium text-sm"
                style={{ color: '#1677ff' }}
                onClick={(e) => e.stopPropagation()}
              >
                {r.student_name}
              </Link>
            ) : (
              <span className="font-medium text-sm">{r.student_name}</span>
            )}
            {/*
              WhatsApp hatırlatması. Veli uygulaması yayılana kadar mesajlar
              elle gönderiliyor; bu ikon `wa.me` bağlantısını açıyor, numara
              ve metin hazır geliyor, GÖNDER'e yönetici basıyor.

              `r.whatsapp` alanını backend YALNIZCA şube müdürü ve üstüne
              koyuyor ve velisi/numarası yoksa hiç koymuyor — yani burada
              ayrıca rol kontrolü yapmaya gerek yok, alan yoksa ikon da yok.
              (Arayüzde gizlemek yetki değildir; asıl kapı sunucuda.)

              stopPropagation şart: satıra tıklamak öğrenci notlarını açıyor.
            */}
            {r.whatsapp && (
              <Tooltip title={`WhatsApp: ${r.whatsapp.parent_name}`}>
                <a
                  href={`https://wa.me/${r.whatsapp.phone}?text=${encodeURIComponent(r.whatsapp.text)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: '#25D366', lineHeight: 1 }}
                  aria-label={`${r.student_name} velisine WhatsApp mesajı gönder`}
                >
                  <WhatsAppOutlined style={{ fontSize: 15 }} />
                </a>
              </Tooltip>
            )}
          </span>
          <div className="flex items-center gap-1 mt-0.5">
            <Tag color={TYPE_COLORS[r.enrollment_type] || 'blue'} style={{ fontSize: 10, margin: 0 }}>
              {TYPE_LABELS[r.enrollment_type] || r.enrollment_type}
            </Tag>
            {r.is_first_lesson && (
              <Tag icon={<StarOutlined />} color="gold" style={{ fontSize: 10, margin: 0 }}>
                İlk Ders
              </Tag>
            )}
            {r.is_birthday && (
              <Tag color="magenta" style={{ fontSize: 10, margin: 0 }}>
                🎂 {r.birthday_date === dayjs().format('YYYY-MM-DD')
                  ? 'Doğum Günü'
                  : `Doğum Günü: ${dayjs(r.birthday_date).format('dddd')}`}
              </Tag>
            )}
            {/* ARDIŞIK DERS. Çocuk çoğu zaman iki saat kalacağını kendisi
                bilmiyor, öğretmen de ders bitince gönderiyordu. Sunucuda
                anlık hesaplanıyor (rozet tablosunda satırı yok) — doğum
                günü etiketiyle aynı yaklaşım.

                Derslik adı YALNIZCA bir sonraki saat başka bir derslikteyse
                geliyor: öğretmen "kalıyor" ile "diğer odaya gidiyor"u
                ayırt edebilsin. */}
            {r.consecutive_hours > 1 && (
              <Tag icon={<ClockCircleOutlined />} color="volcano" style={{ fontSize: 10, margin: 0 }}>
                {r.consecutive_hours} SAAT{r.next_classroom ? ` · ${r.next_classroom}` : ''}
              </Tag>
            )}
            {(r.badges || []).map(b => (
              <Tag key={b.id} color={b.color} style={{ fontSize: 10, margin: 0 }}>
                {b.name}
              </Tag>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: 'Son Video',
      key: 'last_video_at',
      width: 110,
      render: (_, r) => r.last_video_at
        ? <span className="text-xs text-gray-500">{dayjs(r.last_video_at).format('DD.MM.YYYY')}</span>
        : <span className="text-xs text-orange-400">Video yok</span>,
    },
    {
      title: 'Yoklama',
      key: 'status',
      render: (_, r) => (
        <Select
          size="small"
          style={{ width: 150 }}
          placeholder="Seç"
          value={r._status}
          onChange={val => updateRow(r.session_id, '_status', val)}
          options={statusOptions.map(o => ({ value: o.value, label: o.label }))}
        />
      ),
    },
  ];

  // Students who attended (present) for project entry
  const presentStudents = rows.filter(r => r._status === 'present' || r.attendance?.status === 'present');

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <span>
          <span className="font-bold">{classroomName}</span>
          <Text type="secondary" className="ml-2 text-sm">{date} · {startTime}</Text>
        </span>
      }
      width={900}
      footer={null}
      destroyOnClose
    >
      {loading ? (
        <div className="flex justify-center py-8"><Spin /></div>
      ) : (
        <>
          {/* Öğretmen Atama */}
          {CAN_MANAGE_TEACHERS.includes(user?.role) && (
            <div className="mb-4 p-3 rounded-lg bg-gray-50 border">
              <div className="flex items-center gap-2 mb-2">
                <TeamOutlined className="text-gray-500" />
                <span className="text-sm font-semibold">Öğretmenler</span>
              </div>
              <div className="flex gap-2">
                <UserSearchSelect
                  mode="multiple"
                  style={{ flex: 1 }}
                  placeholder="Öğretmen ara..."
                  value={selectedTeachers}
                  onChange={setSelected}
                  extraParams={{ role: ['teacher', 'branch_manager', 'super_admin'] }}
                  initialOptions={slotData?.teachers}
                  renderLabel={(t) => `${t.name} ${t.surname}`}
                />
                <Button icon={<SaveOutlined />} loading={savingTeachers} onClick={handleSaveTeachers}>Kaydet</Button>
              </div>
            </div>
          )}

          {/* Öğrenci Ekle / Telafi Ekle */}
          <div className="flex justify-end gap-2 mb-2">
            {CAN_ADD_MAKEUP.includes(user?.role) && (
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={openMakeupModal}
              >
                Telafi Öğrencisi Ekle
              </Button>
            )}
            {isManager && (
              <Button icon={<UserAddOutlined />} size="small" onClick={() => setAddStudentOpen(true)}>
                Öğrenci Ekle
              </Button>
            )}
          </div>

          {/* Yoklama Tablosu */}
          <Table
            dataSource={rows}
            columns={attendanceColumns}
            rowKey="session_id"
            size="small"
            pagination={false}
            locale={{ emptyText: 'Bu slotta öğrenci yok.' }}
            expandable={{
              expandedRowRender: (record) => (
                <div className="px-4 py-2 bg-gray-50 rounded">
                  <div className="text-xs font-semibold text-gray-500 mb-1">Öğrenci Notları — {record.student_name}</div>
                  <StudentNotePanel studentId={record.student_id} />
                </div>
              ),
              rowExpandable: (record) => !!record.student_id,
            }}
          />

          <div className="mt-3 flex justify-between items-center">
            {isManager && rows.length === 0 && (
              <Popconfirm
                title="Bu dersi silmek istediğinizden emin misiniz?"
                onConfirm={handleDeleteSlot}
                okText="Sil"
                cancelText="İptal"
                okButtonProps={{ danger: true }}
              >
                <Button danger icon={<DeleteOutlined />} loading={deleting}>Dersi Sil</Button>
              </Popconfirm>
            )}
            <div className="ml-auto">
              <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSaveAttendances}>
                Yoklamayı Kaydet
              </Button>
            </div>
          </div>

          <Divider />

          {/* Per-student Ders Projeleri */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className="text-sm font-medium">Ders Projeleri</span>
                <span className="text-xs font-normal text-gray-400 ml-2">(her öğrenci için hangi sertifika/konu işlendi)</span>
              </div>
              <Button icon={<SaveOutlined />} loading={savingProjects} onClick={handleSaveProjects} size="small">
                Projeleri Kaydet
              </Button>
            </div>

            {loadingProjects ? <Spin size="small" /> : (
              presentStudents.length === 0 ? (
                <div className="text-xs text-gray-400 p-3 bg-gray-50 rounded border">
                  Önce yoklama alın — gelen öğrenciler için proje girişi yapabilirsiniz.
                </div>
              ) : (
                presentStudents.map(student => (
                  <StudentProjectRow
                    key={student.student_id}
                    student={student}
                    entries={studentProjects[student.student_id] || []}
                    allCourses={allCourses}
                    topicsByCourse={topicsByCourse}
                    completedTopicIds={studentCompletedTopics[student.student_id] || new Set()}
                    onEntriesChange={handleProjectsChange}
                    onLoadTopics={loadTopicsForCourse}
                    yarisma={!!yarismaByStudent[student.student_id]}
                    onYarismaChange={handleYarismaChange}
                  />
                ))
              )
            )}
          </div>

          <Divider />

          {/* Ders Notu */}
          <div>
            <div className="text-sm font-medium mb-1">
              Ders Notu <span className="text-xs font-normal text-gray-400">(genel not)</span>
            </div>
            <TextArea
              rows={3}
              placeholder="Bu derse ait genel not..."
              value={slotNote}
              onChange={e => setSlotNote(e.target.value)}
              maxLength={2000}
              showCount
            />
            <div className="flex justify-between mt-2">
              {isManager && (
                <Button icon={<BellOutlined />} onClick={() => setNotifyOpen(true)} disabled={!sessionId}>
                  Velilere Bildirim Gönder
                </Button>
              )}
              <Button icon={<SaveOutlined />} loading={saving} onClick={handleSaveNote}>Notu Kaydet</Button>
            </div>
          </div>
        </>
      )}

      {/* Veli Bildirim Modal */}
      <Modal
        open={notifyOpen}
        onCancel={() => { setNotifyOpen(false); notifyForm.resetFields(); }}
        title={<span><BellOutlined className="mr-2" />Velilere Bildirim Gönder</span>}
        footer={null}
        destroyOnClose
        width={480}
      >
        <p className="text-sm text-gray-500 mb-4">
          Bu dersteki tüm öğrencilerin velilerine push bildirimi gönderilecektir.
        </p>
        <Form form={notifyForm} layout="vertical" onFinish={handleNotifyParents}>
          <Form.Item name="title" label="Başlık" rules={[{ required: true, max: 100, message: 'Başlık zorunlu (max 100 karakter).' }]}>
            <Input placeholder="Örn: Ders İptal Edildi" maxLength={100} showCount />
          </Form.Item>
          <Form.Item name="body" label="Mesaj" rules={[{ required: true, max: 500, message: 'Mesaj zorunlu (max 500 karakter).' }]}>
            <TextArea rows={4} placeholder="Örn: Elektrik kesintisi nedeniyle bugünkü ders iptal edilmiştir." maxLength={500} showCount />
          </Form.Item>
          <div className="flex justify-end gap-2">
            <Button onClick={() => { setNotifyOpen(false); notifyForm.resetFields(); }}>İptal</Button>
            <Button type="primary" htmlType="submit" icon={<BellOutlined />} loading={notifying}>
              Gönder
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Öğrenci Ekle Modal */}
      <Modal
        open={addStudentOpen}
        onCancel={() => { setAddStudentOpen(false); setSelectedStudent(null); setStudentSearch(''); setStudentResults([]); setAddStudentType('one_time'); }}
        title={<span><UserAddOutlined className="mr-2" />Öğrenci Ekle — {date} {startTime}</span>}
        footer={null}
        destroyOnClose
        width={480}
      >
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Öğrenci Ara</label>
          <Select
            showSearch
            style={{ width: '100%' }}
            placeholder="İsim veya soyisim ile ara..."
            filterOption={false}
            onSearch={searchStudents}
            onChange={(val) => setSelectedStudent(studentResults.find(s => s.id === val) || null)}
            loading={searchingStudents}
            notFoundContent={studentSearch.length < 2 ? 'En az 2 karakter girin' : 'Öğrenci bulunamadı'}
            value={selectedStudent?.id || undefined}
            allowClear
            onClear={() => setSelectedStudent(null)}
          >
            {studentResults.map(s => (
              <Select.Option key={s.id} value={s.id}>
                {formatStudentLabel(s)}
                {s.status === 'candidate' && <Tag color="orange" className="ml-2" style={{ fontSize: 10 }}>Aday</Tag>}
              </Select.Option>
            ))}
          </Select>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Kayıt Tipi</label>
          <Select style={{ width: '100%' }} value={addStudentType} onChange={setAddStudentType}>
            <Select.Option value="one_time">Tek Seferlik — Sadece o hafta</Select.Option>
            <Select.Option value="trial">Deneme — Sadece tek ders</Select.Option>
            <Select.Option value="makeup">Telafi — Sadece tek ders</Select.Option>
            <Select.Option value="makeup_uncontrolled">Kontrolsüz Telafi — Geçmiş dönem hakkı</Select.Option>
            <Select.Option value="regular">Normal — Her hafta bu güne eklenir</Select.Option>
          </Select>
        </div>
        <div className="flex justify-end gap-2">
          <Button onClick={() => { setAddStudentOpen(false); setSelectedStudent(null); setStudentSearch(''); setStudentResults([]); setAddStudentType('one_time'); }}>İptal</Button>
          <Button type="primary" loading={addingStudent} disabled={!selectedStudent} onClick={handleAddStudent}>
            Ekle
          </Button>
        </div>
      </Modal>

      {/* Telafi Öğrencisi Ekle Modal */}
      <Modal
        open={makeupOpen}
        onCancel={() => { setMakeupOpen(false); setSelectedMakeup(null); }}
        title={<span><PlusOutlined className="mr-2" />Telafi Öğrencisi Ekle — {date} {startTime}</span>}
        footer={null}
        destroyOnClose
        width={520}
      >
        {loadingMakeups ? (
          <div className="flex justify-center py-8"><Spin /></div>
        ) : pendingMakeups.length === 0 ? (
          <div className="text-center text-gray-400 py-6">Bekleyen telafi hakkı olan öğrenci yok.</div>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-3">
              Telafi hakkı olan bir öğrenci seçin. Sadece "bekliyor" durumundaki telafi hakları listelenir.
            </p>
            <Select
              style={{ width: '100%' }}
              placeholder="Öğrenci seç..."
              value={selectedMakeup}
              onChange={setSelectedMakeup}
              optionFilterProp="label"
              showSearch
              allowClear
              options={pendingMakeups.map(m => ({
                value: m.id,
                label: formatStudentLabel(m.student),
                desc: m.attendance?.lesson_session
                  ? `Devamsızlık: ${dayjs(m.attendance.lesson_session.date).format('DD.MM.YYYY')}${m.attendance.lesson_session.start_time ? ' ' + m.attendance.lesson_session.start_time.slice(0, 5) : ''} · ${m.attendance.lesson_session.classroom?.name || ''}`
                  : '',
              }))}
              optionRender={(opt) => (
                <div>
                  <div className="font-medium">{opt.data.label}</div>
                  {opt.data.desc && <div className="text-xs text-gray-400">{opt.data.desc}</div>}
                </div>
              )}
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button onClick={() => { setMakeupOpen(false); setSelectedMakeup(null); }}>İptal</Button>
              <Button type="primary" loading={schedulingMakeup} disabled={!selectedMakeup} onClick={handleScheduleMakeup}>
                Telafi Olarak Ekle
              </Button>
            </div>
          </>
        )}
      </Modal>
    </Modal>
  );
};

export default SlotDetailModal;
